import * as BenchlingUtil from 'BenchlingUtil';
import * as EventConstants from 'constants/EventConstants';
import {LabelTemplate, Printer, PrinterForCreate, PrinterServerResponse} from 'constants/SampleConstants';
import * as LabelPrintingDAO from 'label-printing/LabelPrintingDAO';
import * as LabelTemplateDAO from 'label-printing/LabelTemplateDAO';
import * as PrinterDAO from 'label-printing/PrinterDAO';
import AppDispatcher from 'lib/Dispatcher';
import notifications, {Response} from 'notifications';
import * as RegistryUtil from 'registry/RegistryUtil';

function formatPrinterErrors(errorMessages: Array<string>): string {
  return `Some labels failed to print for the following reasons:\n\t- ${errorMessages.join('\n\t- ')}`;
}

export function printLabels(
  itemIds: Array<string>,
  labelTemplateId: string,
  printerId: string,
  copies: number = 1
): JQueryPromise<{}> {
  return LabelPrintingDAO.printLabels(itemIds, labelTemplateId, printerId, copies)
    .done(({error}: {error: Object}) => {
      notifications.success('Labels sent to printer');
      if (error) {
        const messages = Object.keys(error);
        if (messages.length > 0) {
          notifications.error(formatPrinterErrors(messages));
        }
      }
    })
    .fail(notifications.notifyError);
}

export function fetchPrinters(): void {
  PrinterDAO.fetchPrinters()
    .done(({data}: {data: Array<Printer>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PRINTERS_FETCH_COMPLETED,
        printers: data,
      });
    })
    .fail(notifications.notifyError);
}

export function createPrinter(
  registryId: string,
  ownerUrlId: string,
  {name, address, port, description}: PrinterForCreate
): void {
  PrinterDAO.createPrinter({registryId, name, address, port, description})
    .done(({printer}: PrinterServerResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PRINTER_CREATE_COMPLETED,
        printer,
      });
      BenchlingUtil.navigate(null, RegistryUtil.getRegistryPrintersUrl(ownerUrlId, registryId));
      notifications.success('Created printer successfully');
    })
    .fail(notifications.notifyError);
}

export function updatePrinter(
  printerId: string,
  {name, address, port, description}: {name?: string; address?: string; port?: number; description?: string}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.PRINTER_UPDATE_STARTED,
  });
  PrinterDAO.updatePrinter(printerId, {name, address, port, description})
    .done(({printer}: PrinterServerResponse) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PRINTER_UPDATE_COMPLETED,
        printer,
      });
      notifications.success('Updated printer successfully');
    })
    .fail((response: Response) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.PRINTER_UPDATE_FAILED,
      });
    });
}

export function deletePrinter(printerId: string): void {
  PrinterDAO.deletePrinter(printerId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.PRINTER_DELETE_COMPLETED,
        printerId,
      });
      notifications.success('Printer deleted');
    })
    .fail(notifications.notifyError);
}

export function fetchLabelTemplates(): void {
  LabelTemplateDAO.fetchLabelTemplates()
    .done(({data}: {data: Array<LabelTemplate>}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LABEL_TEMPLATES_FETCH_COMPLETED,
        labelTemplates: data,
      });
    })
    .fail(notifications.notifyError);
}

export function createLabelTemplate(
  registryId: string,
  ownerUrlId: string,
  {name, zplTemplate}: Pick<LabelTemplate, 'name' | 'zplTemplate'>
): void {
  LabelTemplateDAO.createLabelTemplate({registryId, name, zplTemplate})
    .done(({labelTemplate}: {labelTemplate: LabelTemplate}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LABEL_TEMPLATE_CREATE_COMPLETED,
        labelTemplate,
      });
      BenchlingUtil.navigate(null, RegistryUtil.getRegistryLabelTemplatesUrl(ownerUrlId, registryId));
      notifications.success('Created label template successfully');
    })
    .fail(notifications.notifyError);
}

export function updateLabelTemplate(
  labelTemplateId: string,
  {name, zplTemplate}: {name?: string; zplTemplate?: string}
): void {
  AppDispatcher.dispatch({
    actionType: EventConstants.LABEL_TEMPLATE_UPDATE_STARTED,
  });
  LabelTemplateDAO.updateLabelTemplate(labelTemplateId, {name, zplTemplate})
    .done(({labelTemplate}: {labelTemplate: LabelTemplate}) => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LABEL_TEMPLATE_UPDATE_COMPLETED,
        labelTemplate,
      });
      notifications.success('Updated label template successfully');
    })
    .fail((response: Response) => {
      notifications.notifyError(response);
      AppDispatcher.dispatch({
        actionType: EventConstants.LABEL_TEMPLATE_UPDATE_FAILED,
      });
    });
}

export function deleteLabelTemplate(labelTemplateId: string): void {
  LabelTemplateDAO.deleteLabelTemplate(labelTemplateId)
    .done(() => {
      AppDispatcher.dispatch({
        actionType: EventConstants.LABEL_TEMPLATE_DELETE_COMPLETED,
        labelTemplateId,
      });
      notifications.success('Label template deleted');
    })
    .fail(notifications.notifyError);
}
